import { PART_ORIGINS } from "@bt/domain";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  BoxesIcon,
  MapPinIcon,
  PackagePlusIcon,
  SearchIcon,
  ShoppingBagIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ChassisCodeList } from "@/components/app/chassis-codes";
import { CurrentVehicleNotice } from "@/components/app/current-vehicle";
import { EmptyState } from "@/components/app/empty-state";
import { FileImage } from "@/components/app/file-image";
import { Page, PageHeader } from "@/components/app/page";
import { PART_ORIGIN_ICONS, PartOriginBadge } from "@/components/app/part-origin";
import { StockStepper } from "@/components/app/qty";
import { StockStatusChip } from "@/components/app/status";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Segmented, Skeleton } from "@/components/ui/misc";
import { NONE, Select } from "@/components/ui/select";
import { QuickPurchaseDialog } from "@/features/orders/quick-purchase-dialog";
import { PartSheet, TagDot } from "@/features/parts/part-sheet";
import { useCurrentVehicle } from "@/lib/current-vehicle";
import { useFormat } from "@/lib/format";
import { usePriceBasis } from "@/lib/prices";
import { searchEnum, searchText } from "@/lib/search";
import { type RouterOutputs, useTRPC } from "@/lib/trpc";

const STATUS = [
  "all",
  "to_order",
  "missing",
  "low",
  "on_order",
  "in_stock",
  "out_of_stock",
  "archived",
] as const;
type Status = (typeof STATUS)[number];

const searchSchema = z.object({
  q: searchText,
  status: searchEnum(STATUS),
  location: searchText,
  tag: searchText,
  chassis: searchText,
  origin: searchEnum(PART_ORIGINS),
  new: searchText,
});

export const Route = createFileRoute("/inventory/")({
  validateSearch: searchSchema,
  component: InventoryPage,
});

type PartRow = RouterOutputs["parts"]["list"][number];

function InventoryPage() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [query, setQuery] = useState(search.q ?? "");
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const status: Status = search.status ?? "all";

  // Keep the search box responsive; push it to the URL shortly after typing stops.
  useEffect(() => {
    const handle = setTimeout(() => {
      if ((search.q ?? "") !== query)
        void navigate({ search: (s) => ({ ...s, q: query || undefined }), replace: true });
    }, 200);
    return () => clearTimeout(handle);
  }, [query, search.q, navigate]);

  const { vehicleId } = useCurrentVehicle();
  const filter = {
    search: search.q,
    status,
    locationId: search.location ?? null,
    tagId: search.tag ?? null,
    chassisCodeId: search.chassis ?? null,
    origin: search.origin ?? null,
    vehicleId,
  };
  const { data: parts, isLoading } = useQuery({
    ...trpc.parts.list.queryOptions(filter),
    placeholderData: (p) => p,
  });
  const { data: locations = [] } = useQuery(trpc.locations.list.queryOptions());
  const { data: tags = [] } = useQuery(trpc.tags.list.queryOptions());
  const { data: chassisCodes = [] } = useQuery(trpc.chassisCodes.list.queryOptions());
  const { data: overview } = useQuery(trpc.overview.queryOptions({ vehicleId }));

  const filtersActive = Boolean(
    search.q ||
    search.location ||
    search.tag ||
    search.chassis ||
    search.origin ||
    status !== "all",
  );
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    navigate({ search: (s) => ({ ...s, ...patch }), replace: true });

  return (
    <Page wide>
      <PageHeader
        title={t("parts.title")}
        subtitle={parts ? t("parts.count", { count: parts.length }) : " "}
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => setPurchaseOpen(true)}
              className="hidden sm:inline-flex"
            >
              <ShoppingBagIcon />
              {t("orders.quickPurchase")}
            </Button>
            <Button onClick={() => setSearch({ new: "1" })}>
              <PackagePlusIcon />
              {t("parts.new")}
            </Button>
          </>
        }
      />

      <CurrentVehicleNotice />
      <div className="mb-4 flex flex-col gap-3">
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("parts.searchPlaceholder")}
              className="h-10 pl-9"
              aria-label={t("common.search")}
              type="search"
            />
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex">
            <Select
              size="default"
              className="h-10 sm:w-48"
              aria-label={t("parts.location")}
              value={search.location ?? NONE}
              onValueChange={(v) => void setSearch({ location: v === NONE ? undefined : v })}
              options={[
                { value: NONE, label: t("parts.anyLocation") },
                ...locations.map((l) => ({ value: l.id, label: l.path.join(" › ") })),
              ]}
            />
            <Select
              className="h-10 sm:w-40"
              aria-label={t("parts.tags")}
              value={search.tag ?? NONE}
              onValueChange={(v) => void setSearch({ tag: v === NONE ? undefined : v })}
              options={[
                { value: NONE, label: t("parts.anyTag") },
                ...tags.map((tag) => ({
                  value: tag.id,
                  label: (
                    <span className="flex items-center gap-2">
                      <TagDot color={tag.color} />
                      {tag.name}
                    </span>
                  ),
                })),
              ]}
            />
            {chassisCodes.length || search.chassis ? (
              <Select
                className="h-10 sm:w-40"
                aria-label={t("chassisCodes.title")}
                value={search.chassis ?? NONE}
                onValueChange={(v) => void setSearch({ chassis: v === NONE ? undefined : v })}
                options={[
                  { value: NONE, label: t("chassisCodes.any") },
                  ...chassisCodes.map((code) => ({ value: code.id, label: code.name })),
                ]}
              />
            ) : null}
            <Select
              className="h-10 sm:w-40"
              aria-label={t("parts.origin")}
              value={search.origin ?? NONE}
              onValueChange={(v) =>
                void setSearch({
                  origin: v === NONE ? undefined : (v as (typeof PART_ORIGINS)[number]),
                })
              }
              options={[
                { value: NONE, label: t("parts.allOrigins") },
                ...PART_ORIGINS.map((origin) => {
                  const Icon = PART_ORIGIN_ICONS[origin];
                  return {
                    value: origin,
                    label: (
                      <span className="flex items-center gap-2">
                        <Icon className="size-3.5" />
                        {t(`partOrigin.${origin}`)}
                      </span>
                    ),
                  };
                }),
              ]}
            />
          </div>
        </div>
        <Segmented
          aria-label={t("common.status")}
          value={status}
          onChange={(value) => void setSearch({ status: value === "all" ? undefined : value })}
          options={[
            { value: "all", label: t("parts.filterAll") },
            {
              value: "to_order",
              label: t("parts.filterToOrder"),
              count: overview?.counts.toOrderTotal,
            },
            { value: "missing", label: t("parts.filterMissing") },
            { value: "low", label: t("parts.filterLow"), count: overview?.counts.lowStock },
            { value: "on_order", label: t("parts.filterOnOrder") },
            { value: "in_stock", label: t("parts.filterInStock") },
            { value: "out_of_stock", label: t("parts.filterOutOfStock") },
            { value: "archived", label: t("parts.filterArchived") },
          ]}
        />
      </div>

      {isLoading ? (
        <ListSkeleton />
      ) : !parts?.length ? (
        filtersActive ? (
          <EmptyState
            icon={SearchIcon}
            title={t("parts.noMatchTitle")}
            body={t("parts.noMatchBody")}
            action={
              <Button
                variant="outline"
                onClick={() => {
                  setQuery("");
                  void navigate({ search: {}, replace: true });
                }}
              >
                <XIcon />
                {t("parts.clearFilters")}
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={BoxesIcon}
            title={t("parts.emptyTitle")}
            body={t("parts.emptyBody")}
            action={
              <Button onClick={() => setSearch({ new: "1" })}>
                <PackagePlusIcon />
                {t("parts.new")}
              </Button>
            }
          />
        )
      ) : (
        <PartList parts={parts} />
      )}

      <PartSheet
        open={search.new === "1"}
        onOpenChange={(open) => void setSearch({ new: open ? "1" : undefined })}
        onSaved={(id) =>
          void navigate({ to: "/inventory/$partId", params: { partId: id }, search: {} })
        }
      />
      <QuickPurchaseDialog open={purchaseOpen} onOpenChange={setPurchaseOpen} />
    </Page>
  );
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 6 }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static placeholders
        <Skeleton key={i} className="h-16 w-full rounded-xl" />
      ))}
    </div>
  );
}

function PartList({ parts }: { parts: PartRow[] }) {
  const { t } = useTranslation();
  const format = useFormat();
  const priceBasis = usePriceBasis();
  const navigate = useNavigate();
  const open = (id: string) => void navigate({ to: "/inventory/$partId", params: { partId: id } });

  return (
    <>
      {/* Desktop table */}
      <div className="hidden overflow-hidden rounded-xl border border-border bg-surface shadow-xs md:block">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/50 text-left text-xs font-medium text-muted-foreground">
            <tr>
              <th className="py-2.5 pr-3 pl-4 font-medium">{t("parts.columnPart")}</th>
              <th className="hidden px-3 font-medium lg:table-cell">{t("parts.columnLocation")}</th>
              <th className="hidden px-3 text-right font-medium whitespace-nowrap lg:table-cell">
                {t("prices.column")} <span className="font-normal">({priceBasis})</span>
              </th>
              <th className="px-3 font-medium">{t("figures.onHand")}</th>
              <th className="hidden px-3 text-right font-medium xl:table-cell">
                {t("figures.required")}
              </th>
              <th className="hidden px-3 text-right font-medium xl:table-cell">
                {t("figures.onOrder")}
              </th>
              <th className="py-2.5 pr-4 pl-3 font-medium">{t("parts.columnStatus")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {parts.map((part) => (
              <tr
                key={part.id}
                onClick={() => open(part.id)}
                className="group cursor-pointer transition-colors [content-visibility:auto] hover:bg-muted/50"
              >
                <td className="py-2.5 pr-3 pl-4">
                  <div className="flex items-center gap-3">
                    <FileImage
                      fileId={part.coverFileId}
                      alt=""
                      icon={BoxesIcon}
                      className="size-10"
                    />
                    <div className="min-w-0">
                      <Link
                        to="/inventory/$partId"
                        params={{ partId: part.id }}
                        onClick={(e) => e.stopPropagation()}
                        className="line-clamp-2 font-medium outline-none group-hover:text-foreground focus-visible:underline"
                      >
                        {part.name}
                      </Link>
                      {/* Long names and the second line wrap, so the table fits next to the sidebar. */}
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                        {part.primaryNumber ? (
                          <span className="truncate font-mono">{part.primaryNumber}</span>
                        ) : null}
                        {part.manufacturer ? (
                          <span className="truncate">· {part.manufacturer}</span>
                        ) : null}
                        <PartOriginBadge origin={part.origin} />
                        {part.tags.slice(0, 3).map((tag) => (
                          <span key={tag.id} className="hidden items-center gap-1 2xl:flex">
                            <TagDot color={tag.color} />
                            {tag.name}
                          </span>
                        ))}
                      </div>
                      {part.chassisCodes.length ? (
                        <div className="flex text-xs text-muted-foreground">
                          <ChassisCodeList codes={part.chassisCodes} />
                        </div>
                      ) : null}
                    </div>
                  </div>
                </td>
                <td className="hidden max-w-56 px-3 text-muted-foreground lg:table-cell">
                  {part.location ? (
                    <span className="flex items-center gap-1.5 truncate">
                      <MapPinIcon className="size-3.5 shrink-0" />
                      <span className="truncate">
                        {part.location.path.at(-1)}
                        {part.locationDetail ? ` · ${part.locationDetail}` : ""}
                      </span>
                    </span>
                  ) : null}
                </td>
                <td className="hidden px-3 text-right whitespace-nowrap tabular lg:table-cell">
                  {part.price ? format.moneyRange(part.price) : "–"}
                </td>
                <td className="px-3">
                  {part.archived ? (
                    <span className="tabular">{format.qty(part.figures.onHand, part.unit)}</span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <StockStepper
                        partId={part.id}
                        partName={part.name}
                        onHand={part.figures.onHand}
                        unit={part.unit}
                      />
                      <span className="text-xs text-muted-foreground">
                        {t(`unitShort.${part.unit}`)}
                      </span>
                    </div>
                  )}
                </td>
                <td className="hidden px-3 text-right text-muted-foreground tabular xl:table-cell">
                  {part.figures.required ? format.qtyNumber(part.figures.required, part.unit) : "–"}
                </td>
                <td className="hidden px-3 text-right text-muted-foreground tabular xl:table-cell">
                  {part.figures.onOrder ? format.qtyNumber(part.figures.onOrder, part.unit) : "–"}
                </td>
                <td className="py-2.5 pr-4 pl-3">
                  <StockStatusChip
                    figures={part.figures}
                    unit={part.unit}
                    archived={part.archived}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Phone cards */}
      <ul className="flex flex-col gap-2 md:hidden">
        {parts.map((part) => (
          <li key={part.id} className="[content-visibility:auto]">
            <div className="flex items-center gap-3 rounded-xl border border-border bg-surface p-3 shadow-xs">
              <Link
                to="/inventory/$partId"
                params={{ partId: part.id }}
                className="flex min-w-0 flex-1 items-center gap-3"
              >
                <FileImage fileId={part.coverFileId} alt="" icon={BoxesIcon} className="size-12" />
                <div className="min-w-0 flex-1">
                  <div className="line-clamp-2 leading-snug font-medium">{part.name}</div>
                  {part.primaryNumber || part.price ? (
                    <div className="flex gap-2 text-xs text-muted-foreground">
                      {part.primaryNumber ? (
                        <span className="truncate font-mono">{part.primaryNumber}</span>
                      ) : null}
                      {part.price ? (
                        <span className="shrink-0 tabular">{format.moneyRange(part.price)}</span>
                      ) : null}
                    </div>
                  ) : null}
                  {part.chassisCodes.length ? (
                    <div className="flex text-xs text-muted-foreground">
                      <ChassisCodeList codes={part.chassisCodes} />
                    </div>
                  ) : null}
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    <StockStatusChip
                      figures={part.figures}
                      unit={part.unit}
                      archived={part.archived}
                    />
                    <PartOriginBadge origin={part.origin} />
                  </div>
                </div>
              </Link>
              {!part.archived ? (
                <div className="flex flex-col items-end gap-1">
                  <StockStepper
                    partId={part.id}
                    partName={part.name}
                    onHand={part.figures.onHand}
                    unit={part.unit}
                  />
                  <span className="text-[11px] text-muted-foreground">
                    {t(`unitShort.${part.unit}`)}
                  </span>
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}
